import { zodResolver } from '@hookform/resolvers/zod';
import { useHeaderHeight } from '@react-navigation/elements';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import React from 'react';
import { useForm } from 'react-hook-form';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import {
  useCustomerEmailVerify,
  useCustomerEmailVerifyRequest,
} from '@/api/customers/use-email-verify';
import { useUpdateCustomer } from '@/api/customers/use-update-customer';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { GREY_40 } from '@/style/colors';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

type EmailFormProps = {
  email: string;
};

const emailFormSchema = z.object({
  email: z.string().email(),
});

export type NameFormType = z.infer<typeof emailFormSchema>;

export default function ProfileEmail() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const pathname = usePathname();
  const headerHeight = useHeaderHeight();
  const queryClient = useQueryClient();
  const { token } = useLocalSearchParams();

  const { mutate: updateCustomer, isPending: isUpdatingCustomer } =
    useUpdateCustomer();

  const {
    mutate: sendEmailVerifyRequest,
    isPending: isSendingEmailVerificationRequest,
  } = useCustomerEmailVerifyRequest();

  const { mutateAsync: verifyEmail, isPending: isVerifyingEmail } =
    useCustomerEmailVerify();

  const { data: customer, refetch: refetchCustomer } = useCustomer();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
  } = useForm<NameFormType>({
    defaultValues: {
      email: customer?.email,
    },
    resolver: zodResolver(emailFormSchema),
  });

  React.useEffect(() => {
    if (customer?.email) {
      setValue('email', customer.email);
    }
  }, [customer?.email]);

  React.useEffect(() => {
    const handleVerifyEmail = async () => {
      try {
        await verifyEmail({ token: token as string });
        Toast.show({
          type: 'success',
          text1: 'Email verified successfully',
        });

        refetchCustomer();
      } catch (e) {
        showErrorMessage(e.message);
      }
    };

    if (token && customer && !customer?.emailVerified) {
      handleVerifyEmail();
    }
  }, [token, customer]);

  const handleContinue = (data: EmailFormProps) => {
    updateCustomer(
      {
        email: data.email,
      },
      {
        onSuccess: (d) => {
          queryClient.setQueryData(['customers/me'], d.data);

          Toast.show({
            type: 'success',
            text1: 'Email updated successfully',
          });
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleResend = () => {
    sendEmailVerifyRequest(
      {},
      {
        onSuccess: () => {
          Toast.show({
            type: 'success',
            text1: 'Email verification sent!',
          });
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  if (isUpdatingCustomer || isSendingEmailVerificationRequest) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        alignItems: 'center',
      }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={headerHeight}
    >
      <View style={styles.container}>
        {isDesktop() ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (pathname.includes('/partner')) {
                  router.navigate('/partner/more/profile');
                  return;
                }

                router.navigate('/account/profile');
              }}
            />

            <Text type="largeTitle">Email</Text>
          </View>
        ) : null}

        <View style={styles.contentContainer}>
          <View style={{ gap: 24 }}>
            {/* <Text
                    tx={
                      customer?.emailVerified
                        ? 'profileEmail.verified'
                        : 'profileEmail.notVerified'
                    }
                    type="bodyRegular"
                    style={{
                      color: customer?.emailVerified
                        ? theme.colors.input.success.helperText
                        : theme.colors.input.error.helperText,
                    }}
                  /> */}

            <Text
              tx="profileEmail.verifyInfo"
              type="bodyRegular"
              style={{ color: GREY_40 }}
            />

            <ControlledInput
              control={control}
              name="email"
              label="Email"
              spellCheck={false}
              autoComplete="email"
              onBlur={() => {
                trigger('email');
              }}
              onChangeText={(text) => {
                trigger('email');
                setValue('email', text);
              }}
            />

            {/* {!customer?.emailVerified && (
                    <Button
                      tx="profileEmail.resend"
                      type="ghost"
                      onPress={handleResend}
                      style={{ alignSelf: 'center' }}
                    />
                  )} */}
          </View>

          <View>
            <Button
              type="primary"
              tx="profileEmail.update"
              onPress={handleSubmit(handleContinue)}
              disabled={!isValid || customer?.email === getValues().email}
            />
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
  },
}));
